/**
 * Fire-and-forget client checkout events → server `logs/stripe.json`.
 * Never blocks checkout; never sends card numbers or client secrets.
 */

export type StripeCheckoutLogLevel = 'info' | 'warn' | 'error';

export interface StripeCheckoutLogContext {
    apiBaseUrl: string;
    getAuthToken?: () => Promise<string | null>;
}

/** Strip Stripe error objects down to safe diagnostic fields. */
export function stripeErrorSnapshot(error: unknown): Record<string, unknown> {
    if (!error || typeof error !== 'object') {
        return error != null ? { message: String(error) } : {};
    }
    const e = error as Record<string, unknown>;
    const pi = e.payment_intent as Record<string, unknown> | undefined;
    return {
        type: e.type,
        code: e.code,
        decline_code: e.decline_code,
        message: e.message,
        param: e.param,
        doc_url: e.doc_url,
        payment_intent_id: typeof pi?.id === 'string' ? pi.id : undefined,
        payment_intent_status: typeof pi?.status === 'string' ? pi.status : undefined,
        last_payment_error_message: (e.last_payment_error as Record<string, unknown> | undefined)?.message
            ?? (pi?.last_payment_error as Record<string, unknown> | undefined)?.message,
        last_payment_error_decline_code: (e.last_payment_error as Record<string, unknown> | undefined)?.decline_code
            ?? (pi?.last_payment_error as Record<string, unknown> | undefined)?.decline_code,
    };
}

export async function logStripeCheckoutEvent(
    ctx: StripeCheckoutLogContext,
    event: string,
    data?: Record<string, unknown>,
    level: StripeCheckoutLogLevel = 'info',
): Promise<void> {
    if (!ctx.apiBaseUrl || !ctx.getAuthToken) return;

    try {
        const token = await ctx.getAuthToken();
        if (!token) return;

        await fetch(`${ctx.apiBaseUrl}/api/stripe/checkout-log`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                event,
                level,
                data: {
                    ...data,
                    page: typeof window !== 'undefined' ? window.location.pathname : undefined,
                },
            }),
            keepalive: true,
        });
    } catch {
        // Non-blocking — checkout must not fail because logging failed.
    }
}
